import { ReactNode, useCallback, useMemo, useRef, useState } from "react";
import { pluralizeWithCount } from "@common/helpers/string";
import {
    AccountingSchedulesView,
    AccountingSchedulesViewSchema,
} from "@common/types/views/accountingSchedulesView";
import { TablePageHeader } from "@components/pageHeader/TablePageHeader";
import { EntityFilterCombobox } from "@components/entity/EntityFilter/EntityFilterCombobox";
import { Button } from "@design-system/Button/Button";
import { WithHotkey } from "@design-system/Button/WithHotkey";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { CommandIslandTotal } from "@design-system/CommandIsland/CommandIslandTotal";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { HStack } from "@design-system/HStack/HStack";
import { Loader } from "@design-system/Loader/Loader";
import { ColumnSettings } from "@design-system/ViewToolbar/ColumnSettings";
import { useEntitiesById } from "@hooks/useEntities";
import {
    WORKSPACE_USER_CACHE_KEYS,
    useWorkspaceUserCache,
} from "@hooks/useWorkspaceUserCache";
import { useDynamicTopOffset } from "@design-system/hooks/useDynamicTopOffset";
import { styleWithVars } from "@lib/cssVars";
import { mergeCachedView } from "@lib/mergeCachedView";
import {
    EMPTY_SCHEDULE_TEMPLATE_FILTERS,
    ScheduleTemplateFilters,
    buildScheduleGroups,
    collectScheduleTemplateGroups,
    DEFAULT_SCHEDULES_SORT,
    filterScheduleTemplates,
    hasActiveScheduleTemplateFilters,
} from "./scheduleListHelpers";
import { SchedulePoliciesFiltersControls } from "./SchedulePoliciesFiltersControls";
import { ScheduleTemplateActions } from "./ScheduleRowActionsCell";
import { SchedulesTable } from "./SchedulesTable";
import {
    SCHEDULES_TABLE_DEFAULT_VIEW,
    schedulesTableViewConfig,
} from "./schedulesTableViewConfig";
import styles from "./SchedulesPage.module.scss";
import { toSortedPolicyFilterOptions } from "./policyFilterOptions";
import { useUrlScopedTemplateIds } from "./useUrlScopedTemplateIds";
import {
    useScheduleHubCopy,
    useScheduleHubTemplates,
} from "./ScheduleHubScopeProvider";

interface SchedulesListProps extends ScheduleTemplateActions {
    onCreate: () => void;
    onNewGroup: () => void;
    tabNavigationTop: ReactNode;
    templateId: string | null;
    onTemplateIdChange: (templateId: string | null) => void;
}

function renderSchedulesEmptyView({
    isError,
    hasNarrowedResults,
    onClearNarrowing,
    onCreate,
    emptyBody,
}: {
    isError: boolean;
    hasNarrowedResults: boolean;
    onClearNarrowing: () => void;
    onCreate: () => void;
    emptyBody: string;
}) {
    if (isError) {
        return (
            <EmptyState
                header="Couldn't load policies"
                body="Something went wrong loading policies. Please try again."
            />
        );
    }
    if (hasNarrowedResults) {
        return (
            <EmptyState
                header="No matching policies"
                body="Try a different search or clear the filters."
                ctaText="Clear search and filters"
                action={onClearNarrowing}
            />
        );
    }
    return (
        <EmptyState
            header="No policies yet"
            body={emptyBody}
            ctaText="Create policy"
            action={onCreate}
        />
    );
}

export const SchedulesList: React.FC<SchedulesListProps> = ({
    onCreate,
    onNewGroup,
    onApply,
    onEdit,
    onViewApplied,
    onDuplicate,
    onArchive,
    onRestore,
    onDelete,
    tabNavigationTop,
    templateId,
    onTemplateIdChange,
}) => {
    const templatesQuery = useScheduleHubTemplates({
        includeArchived: true,
        includeMilestone: true,
    });
    const { policiesEmptyStateBody } = useScheduleHubCopy();
    const entitiesById = useEntitiesById({ onlyBusiness: true });
    const containerRef = useRef<HTMLDivElement>(null);
    const { topOffset } = useDynamicTopOffset({ containerRef });
    const [search, setSearch] = useState("");
    const [filters, setFilters] = useState<ScheduleTemplateFilters>(() => ({
        ...EMPTY_SCHEDULE_TEMPLATE_FILTERS,
        templateIds: templateId ? [templateId] : [],
    }));

    const syncTemplateIdsToUrl = useUrlScopedTemplateIds({
        templateId,
        onTemplateIdChange,
        setFilters,
    });

    const handleFiltersChange = useCallback(
        (nextFilters: ScheduleTemplateFilters) => {
            setFilters(nextFilters);
            syncTemplateIdsToUrl(nextFilters.templateIds);
        },
        [syncTemplateIdsToUrl],
    );

    const [cachedView, setCachedView] =
        useWorkspaceUserCache<AccountingSchedulesView>(
            WORKSPACE_USER_CACHE_KEYS.SCHEDULES_PAGE_VIEW_CACHE_KEY,
            AccountingSchedulesViewSchema,
        );
    const [view, setView] = useState<AccountingSchedulesView>(() =>
        mergeCachedView(
            cachedView,
            SCHEDULES_TABLE_DEFAULT_VIEW,
            schedulesTableViewConfig,
        ),
    );
    const handleViewChange = useCallback(
        (nextView: AccountingSchedulesView) => {
            setView(nextView);
            setCachedView(nextView);
        },
        [setCachedView],
    );

    const groupNames = useMemo(
        () => collectScheduleTemplateGroups(templatesQuery.data ?? []),
        [templatesQuery.data],
    );
    const policies = useMemo(
        () => toSortedPolicyFilterOptions(templatesQuery.data ?? []),
        [templatesQuery.data],
    );

    const groups = useMemo(() => {
        if (templatesQuery.isError) {
            return [];
        }
        if (templatesQuery.data == null) {
            return null;
        }
        return buildScheduleGroups(
            filterScheduleTemplates({
                templates: templatesQuery.data,
                search,
                filters,
            }),
            DEFAULT_SCHEDULES_SORT,
        );
    }, [filters, search, templatesQuery.data, templatesQuery.isError]);

    const visibleCount =
        groups?.reduce((count, group) => count + group.templates.length, 0) ??
        0;
    const hasNarrowedResults =
        search.trim() !== "" || hasActiveScheduleTemplateFilters(filters);

    const renderBody = () => {
        if (groups == null) {
            return (
                <div className={styles.emptyState}>
                    <Loader />
                </div>
            );
        }
        if (groups.length === 0) {
            return (
                <div className={styles.emptyState}>
                    {renderSchedulesEmptyView({
                        isError: templatesQuery.isError,
                        hasNarrowedResults,
                        onClearNarrowing: () => {
                            setSearch("");
                            handleFiltersChange(
                                EMPTY_SCHEDULE_TEMPLATE_FILTERS,
                            );
                        },
                        onCreate,
                        emptyBody: policiesEmptyStateBody,
                    })}
                </div>
            );
        }
        return (
            <SchedulesTable
                groups={groups}
                columns={view.columns.map((column) => column.id)}
                entitiesById={entitiesById}
                onApply={onApply}
                onEdit={onEdit}
                onViewApplied={onViewApplied}
                onDuplicate={onDuplicate}
                onArchive={onArchive}
                onRestore={onRestore}
                onDelete={onDelete}
            />
        );
    };

    return (
        <>
            <TablePageHeader
                isFullPage
                borderBottom
                search={search}
                onSearchChange={setSearch}
                searchPlaceholder="Search policies..."
                tabNavigationTop={tabNavigationTop}
                afterFiltersChildren={
                    <SchedulePoliciesFiltersControls
                        filters={filters}
                        onFiltersChange={handleFiltersChange}
                        groupNames={groupNames}
                        policies={policies}
                    />
                }
            >
                <HStack gap={8} align="center">
                    <EntityFilterCombobox
                        filters={{ entityIds: filters.entityIds }}
                        onChange={({ entityIds }) =>
                            handleFiltersChange({
                                ...filters,
                                entityIds: entityIds ?? [],
                            })
                        }
                        onlyBusiness
                    />
                    <ColumnSettings
                        view={view}
                        viewConfig={schedulesTableViewConfig}
                        defaultView={SCHEDULES_TABLE_DEFAULT_VIEW}
                        onViewChange={handleViewChange}
                        isDensityHidden
                    />
                    <Button variant="primary" size="md" onClick={onNewGroup}>
                        Add policy group
                    </Button>
                    <WithHotkey
                        hotKey="alt+n"
                        hotKeyOptions={{
                            description: "Add policy",
                            preventDefault: true,
                        }}
                    >
                        <Button variant="primary" size="md" onClick={onCreate}>
                            Add policy
                        </Button>
                    </WithHotkey>
                </HStack>
            </TablePageHeader>
            <div
                ref={containerRef}
                className={styles.tableContainer}
                style={styleWithVars({
                    "--schedules-top-offset": `${topOffset}px`,
                })}
            >
                {renderBody()}
                {groups != null && groups.length > 0 && (
                    <div className={styles.footerIsland}>
                        <CommandIsland sticky>
                            <CommandIslandTotal>
                                {pluralizeWithCount("policy", visibleCount)}
                            </CommandIslandTotal>
                        </CommandIsland>
                    </div>
                )}
            </div>
        </>
    );
};
